import { GetServerSideProps } from 'next';
import ProductDetail from '@/Page/ProductDetail';
import {
  IInitialServerProductDetail,
  getInitialProductDetail,
} from '@/Page/ProductDetail/Context/ServerState/actions/getInitialProductDetail';
import { IFullProductItem } from '@/Models/Product/IProduct';
import { getImageUrlByUrl } from '@/Services/Image';
import { NextSeo } from 'next-seo';
import { WEBSITE_URL } from '@/Data/Constants';
import { useTranslation } from 'next-i18next';
import { getNameKeyByLang } from '@/Utils/getNameKeyByLang';
import numberService from '@/Services/NumberService';
import { getProductDetailUrl } from '@/Utils/getProductDetailUrl';
import { getTokenOnServerSide } from '@/Services/SsrService/getTokenOnServerSide';
import { sharedServerSideLogic } from '@/Services/SsrService/sharedServerSideLogic';
import { pageLinks } from '@/Data/pageLinks';
import Head from 'next/head';

// NOTE: BASE/item/ID-NAME
export default function ProductDetailPage({ initialProductDetailData }: { initialProductDetailData: IInitialServerProductDetail }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as 'uz' | 'ru';
  const product = initialProductDetailData.data as IFullProductItem;

  const link_to_page = product.slug
    ? `${WEBSITE_URL}/item/${product.slug}`
    : `${WEBSITE_URL}${getProductDetailUrl(product.name, product.id)}`;

  const JSON_LD_DATA = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image:
      product.images?.map(image => {
        return getImageUrlByUrl(image.path);
      }) ?? [],
    sku: product.id,
    offers: {
      '@type': 'Offer',
      url: link_to_page,
      price: product.productPrice?.price,
      priceCurrency: product.productPrice?.currencyType,
      itemCondition: product.productState === 'NEW' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Person',
        name: product.user?.firstName,
      },
    },
  };
  return (
    <>
      <NextSeo
        // title={`${product.name}: ${numberService.insertSpace(product.productPrice?.price)} ${t(product.productPrice?.currencyType)} ${product.category[getNameKeyByLang(language)]} - Joyla`}
        // description={product.description}
        title={
          t('common:seo_info.productDetail.title', {
            name: product.name,
            price: numberService.insertSpace(product.productPrice?.price) + ' ' + t(product.productPrice?.currencyType),
            categoryName: product.category[getNameKeyByLang(language)],
            id: product.id,
          })!
        }
        description={
          t('common:seo_info.productDetail.title', {
            name: product.name,
            price: numberService.insertSpace(product.productPrice?.price) + ' ' + t(product.productPrice?.currencyType),
            categoryName: product.category[getNameKeyByLang(language)],
            id: product.id,
          })!
        }
        canonical={link_to_page}
        openGraph={{
          url: link_to_page,
          title: t('common:seo_info.productDetail.title', {
            name: product.name,
            price: numberService.insertSpace(product.productPrice?.price) + ' ' + t(product.productPrice?.currencyType),
            categoryName: product.category[getNameKeyByLang(language)],
            id: product.id,
          })!,
          description: product.description,
          images: [
            {
              url: getImageUrlByUrl(product.images[0].path),
              alt: 'Joyla',
            },
          ],
          siteName: 'Joyla',
        }}
      />
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(JSON_LD_DATA, undefined, 2) }} />
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: product.name,
            description: product.description,
            sku: link_to_page,
            image: [
              {
                url: getImageUrlByUrl(product.images[0].path),
                alt: 'Joyla',
              },
            ],
            offers: {
              '@type': 'Offer',
              priceCurrency: product.productPrice?.currencyType,
              price: numberService.insertSpace(product.productPrice?.price),
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Person',
                name: product.user?.firstName,
              },
            },
          })}
        </script>
      </Head>

      <>
        <ProductDetail key={product.id} initialData={initialProductDetailData} />
      </>
    </>
  );
}

function getIdByString(url: string): number {
  const strs = url.split('-');
  return +strs[strs.length - 1];
}

const fn: GetServerSideProps = async context => {
  const slug = (context.query.slug as string[]) || [];
  const productId = getIdByString(slug[0]);
  const client_token = getTokenOnServerSide(context);
  const data = await getInitialProductDetail(productId, client_token);

  const notActiveProduct = data.data?.status !== 'PUBLISHED';

  return {
    props: { initialProductDetailData: data },
    ...(!data.data || notActiveProduct
      ? {
          redirect: {
            destination: pageLinks.itemNotFound,
            permanent: false,
          },
        }
      : {}),
  };
};

export const getServerSideProps: GetServerSideProps = sharedServerSideLogic(fn);
